import {
  cloneElement,
  ComponentType,
  CSSProperties,
  forwardRef,
  ReactElement,
  ReactNode
} from 'react';
import clsx from 'clsx';
import { Icon } from '@namespace/icons2';
import { useGetThemeConfig } from '@namespace/themes';
import { propsMerge } from '../../utils/propsMerge';
import { Box } from '../Box';
import { ActionTypes, Indents } from './types';

import styles from './index.module.css';

const iconOnlySizesMap: Record<string, string> = {
  xs: 's',
  s: 'm',
  m: 'l',
  l: 'l',
  xl: 'xl'
};

interface ButtonProps {
  className?: string;
  contentClass?: string;
  intent?: Indents;
  size?: 's' | 'm' | 'l' | 'xl';
  to?: string; // for LocalizedLink cases
  component?: ComponentType<unknown> | string;
  children?: ReactNode;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  elementLeft?: ReactNode;
  elementRight?: ReactNode;
  fullWidth?: boolean;
  actionType?: ActionTypes;
  disabled?: boolean;
  iconOnly?: boolean;
  processing?: boolean;
  dataRole?: string;
  styles?: CSSProperties;
  customIconSize?: string;
  isNextButton?: boolean;
  type?: string;
  onClick?: () => void;
}

export const Button = forwardRef<HTMLElement, ButtonProps>((props, ref) => {
  const buttonConfig = useGetThemeConfig('Button');
  const {
    className,
    contentClass,
    intent = 'primary',
    size = 'm',
    component: Component = 'button',
    children,
    iconLeft,
    iconRight,
    elementLeft,
    elementRight,
    fullWidth,
    actionType = 'color1',
    disabled,
    iconOnly,
    processing,
    dataRole,
    styles: customStyles = {},
    customIconSize,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isNextButton, // remove from DOM
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    rules, // remove from DOM
    ...restProps
  } = propsMerge(props, buttonConfig);

  const type = Component === 'button' ? 'button' : undefined;

  const classes = clsx([
    styles.button,
    styles[intent],
    styles[size],
    styles[actionType],
    className,
    disabled && styles.disabled,
    fullWidth && styles.fullWidth,
    iconOnly && styles.iconOnly,
    processing && styles.processing
  ]);

  const commonIconSize = size === 'xl' ? 'm' : 's';
  const iconSize =
    customIconSize || (iconOnly ? iconOnlySizesMap[size] : commonIconSize);

  const dataRoleProp = {
    [typeof Component === 'string' ? 'data-role' : 'dataRole']: dataRole
  };

  return (
    <Component
      {...dataRoleProp}
      className={classes}
      type={type}
      disabled={disabled}
      ref={ref}
      style={customStyles}
      {...restProps}
    >
      {iconLeft && (
        <Box component="span" className={styles.iconLeft}>
          {cloneElement(iconLeft as ReactElement, { size: iconSize })}
        </Box>
      )}
      {elementLeft && !iconLeft && (
        <Box component="span" className={styles.iconLeft}>
          {elementLeft}
        </Box>
      )}
      {children && !iconOnly && (
        <Box component="span" className={clsx(styles.content, contentClass)}>
          {children}
        </Box>
      )}
      {iconRight && (
        <Box component="span" className={styles.iconRight}>
          {cloneElement(iconRight as ReactElement, { size: iconSize })}
        </Box>
      )}
      {elementRight && !iconRight && (
        <Box component="span" className={styles.iconRight}>
          {elementRight}
        </Box>
      )}
      {processing && (
        <Box className={styles.processingIconContainer}>
          <Icon
            size={iconSize}
            className={styles.processingIcon}
            name="icons/general/state/loader"
          />
        </Box>
      )}
    </Component>
  );
});

/**
 * Default props for Button component need for theme merge correct intent
 */
Button.defaultProps = {
  intent: 'primary'
};
